import { AlertTriangle } from 'lucide-react'
import { dispatch, useSelector } from '@/lib/store'
import { usePathname, useRouter } from 'next/navigation'
import React, { useState } from 'react'
import { Button } from '../ui/button'
import ActionButton from './ActionButton'
import { updateCartData } from '@/lib/reducers/cartReducer'
import { askUserToSelectPatient } from '@/lib/reducers/patientReducer'
import { updateLoginData } from '@/lib/reducers/loginReducer'
import PACKAGE_HELPER from '@/helper/packageHelper'
import useCustomToast from '@/hooks/useCustomToast'
import { DELETE_ACTION, EDOS_PAGE, INSERT_ACTION } from '@/utils/constants'
import CART_HELPER, { isProductInCart } from '@/helper/cartHelper'
import { initSavingDto, updateOrderData } from '@/lib/reducers/orderReducer'
import Link from 'next/link'

const TestAndPackagesCard = ({
  product,
  productDto,
  onOpenChange,
  validationErrorToMap,
  setShowFlag,
  hidePrice,
}: any) => {
  const showToast = useCustomToast()

  const userState = useSelector((state) => state.user) || {}
  const cartState: any = useSelector((state) => state.cart) || {}
  const patientState = useSelector((state) => state.patient) || {}
  const loginState = useSelector((state) => state.login) || {}
  const orderState = useSelector((state) => state.order) || {}
  const cityState = useSelector((state) => state.city) || {}
  const pathname = usePathname()

  const { handleCheckValidationForProduct, getTestOrPackageUrl } =
    PACKAGE_HELPER()
  const { handleGetCartItems, handleAddOrRemoveProductInCart } = CART_HELPER()

  const [addLoading, setAddLoading] = useState('')

  const handleAddProduct = async (item: any) => {
    if (!loginState?.isUserLoggedIn) {
      dispatch(updateLoginData({ ...loginState, loginModalIsOpen: true }))
      return
    }
    const validationResponse = await handleCheckValidationForProduct(item)
    if (validationResponse?.errorMessage) {
      // setValidationError({ productId: item?.productDto?.id, errorMessage: validationResponse?.errorMessage })
      showToast('danger', validationResponse?.errorMessage)
      return
    }
    setAddLoading(item.productDto.id)
    if (
      Array.isArray(userState?.membersList) &&
      userState?.membersList?.length > 1 &&
      (cartState?.activePatientDetailDto === null ||
        Object?.keys(cartState?.activePatientDetailDto).length === 0)
    ) {
      dispatch(
        askUserToSelectPatient({
          ...patientState,
          selectPatientModalIsOpen: true,
          selectedTest: { ...item },
        })
      )
    } else {
      const validationResponse = await handleCheckValidationForProduct(item)
      if (!!validationResponse?.errorMessage) {
        showToast('danger', validationResponse.errorMessage)
        return
      }
      const apiResponse = await handleAddOrRemoveProductInCart(
        INSERT_ACTION,
        userState,
        item
      )
      if (apiResponse?.status === true) {
        if (!!apiResponse?.data[0]?.sameProductAdded) {
          showToast('warning', apiResponse?.data[0]?.message)
        }
        if (!!apiResponse?.data[0]?.cityChanged) {
          showToast('warning', apiResponse?.data[0]?.message)
        }
        const res = await handleGetCartItems()
        dispatch(updateCartData({ ...cartState, ...res?.data }))
        dispatch(
          updateOrderData({
            ...orderState,
            cartDtoList: [...res?.data?.cartDtoList],
            savingDto: initSavingDto,
          })
        )
        setShowFlag(true)
      }
    }
    setAddLoading('')
  }

  const handleRemoveProduct = async (item: any) => {
    if (!loginState?.isUserLoggedIn) {
      dispatch(updateLoginData({ ...loginState, loginModalIsOpen: true }))
      return
    }
    setAddLoading(item?.productDto?.id)
    if (
      Array.isArray(userState?.membersList) &&
      userState?.membersList?.length > 1 &&
      (cartState?.activePatientDetailDto === null ||
        Object?.keys(cartState?.activePatientDetailDto).length === 0)
    ) {
      dispatch(
        askUserToSelectPatient({
          ...patientState,
          selectPatientModalIsOpen: true,
          selectedTest: { ...item },
        })
      )
    } else {
      const apiResponse = await handleAddOrRemoveProductInCart(
        DELETE_ACTION,
        userState,
        item
      )
      if (apiResponse?.status === true) {
        if (!!apiResponse?.data[0]?.cityChanged) {
          showToast('warning', apiResponse?.data[0]?.message)
        }
        const res = await handleGetCartItems()
        dispatch(updateCartData({ ...cartState, ...res?.data }))
        dispatch(
          updateOrderData({
            ...orderState,
            cartDtoList: [...res?.data?.cartDtoList],
            savingDto: initSavingDto,
          })
        )
      }
    }
    setAddLoading('')
  }
  const isInCart = isProductInCart(
    cartState?.cartDtoList,
    product?.productDto?.id
  )

  return (
    <div
      className={`${
        hidePrice ? 'py-2.5' : 'py-3.5'
      } px-5 flex items-center gap-3 border-b border-gray-100 last:border-b-0 group hover:bg-gray-50 transition-colors duration-150`}
      key={`${productDto?.code}_${productDto.id}`}
    >
      <Link
        className="cursor-pointer flex-1 min-w-0 no-underline [color:inherit]"
        href={getTestOrPackageUrl(
          productDto,
          cityState?.currentCityObj?.slugName
        )}
        onClick={() => onOpenChange(false)}
      >
        <h3 className="text-body-sm font-semibold text-gray-900 leading-tight group-hover:text-primary-500 transition-colors duration-200">
          {pathname === EDOS_PAGE ? `${productDto?.code}: ` : ''}
          {productDto?.name}
        </h3>
        {!hidePrice && (
          <div className="flex items-center gap-2 mt-1.5">
            <p className="text-label-md font-bold text-gray-900">
              ₹{productDto?.price}
            </p>
            {productDto?.marketPrice !== productDto?.price && (
              <p className="text-body-xs text-gray-400 line-through">
                ₹{productDto?.marketPrice}
              </p>
            )}
            {!!productDto?.discountPercentage && (
              <span className="text-label-xs font-semibold text-success-700 bg-success-50 px-1.5 py-0.5 rounded">
                {productDto?.discountPercentage}% OFF
              </span>
            )}
          </div>
        )}
        {validationErrorToMap &&
          validationErrorToMap?.productId === productDto?.id && (
            <div className="mt-2 flex items-center gap-1.5">
              <AlertTriangle size={14} className="text-danger-400 shrink-0" />
              <p className="text-label-xs font-semibold text-danger-400">
                {validationErrorToMap?.errorMessage}
              </p>
            </div>
          )}
      </Link>
      <div className="flex-shrink-0">
        {isInCart ? (
          <Button
            title="Remove"
            disabled={addLoading === productDto?.id}
            className="text-label-xs font-semibold bg-white-100 border border-danger-200 text-danger-600 hover:bg-danger-50 h-auto shadow-none rounded-lg py-1.5 px-3 transition-colors"
            onClick={() => handleRemoveProduct(product)}
          >
            Remove
          </Button>
        ) : (
          <ActionButton
            variant="secondary"
            title="Add"
            loading={addLoading === productDto?.id}
            loadingText="..."
            disabled={addLoading === productDto?.id}
            onClick={() => handleAddProduct(product)}
            className="text-label-xs px-4 py-1.5 rounded-lg"
          />
        )}
      </div>
    </div>
  )
}

export default TestAndPackagesCard
